'use client';

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CarOutlined,
  ClockCircleOutlined,
  CompassOutlined,
  EnvironmentFilled,
  LockOutlined,
} from '@ant-design/icons';
import DateButton from './planner/DateButton';
import { isLongLeg, isMeaningfulLeg, isUnstartedLoop, stopRoleText, tripPlaceCount, tripSettingsSummary } from './planner/plannerStart';
import { TripLeg, TripStop } from '../types/trip';
import { dateForTripDay, segmentIntoDays, summariseLegs, TripDay } from '../utils/tripPlanner';
import { fetchWeather, forecastForDate, weatherEmoji } from '../utils/weatherApi';
import { MAX_NIGHTS_PER_STOP, middleStopNights, stopRole, type StopRole } from '../utils/tripRoles';
import { isLoopTrip } from '../utils/tripInsert';

interface TripPlannerLadderProps {
  stops: TripStop[];
  legs: TripLeg[];
  onReorder: (stops: TripStop[]) => void;
  onRemoveStop?: (stopId: string) => void;
  /** When provided, each stop gets an editable note field. */
  onEditNote?: (stopId: string, notes: string) => void;
  /** When provided, stop names are click-to-rename (fixes auto-named pins). */
  onEditName?: (stopId: string, name: string) => void;
  /** Trip departure date (YYYY-MM-DD) — enables real dates + weather per day. */
  startDate?: string | null;
  onChangeStartDate?: (date: string | null) => void;
  /** Daily drive-time budget used to split the ladder into days. */
  maxDriveMinutesPerDay?: number;
  /** When provided, a Drive/day selector lets the user pick the budget. */
  onChangeMaxDriveHours?: (hours: number) => void;
  /** Vehicle the trip is planned for (saved as the trip's vehicle_type). */
  vehicleType?: string;
  onChangeVehicleType?: (type: string) => void;
  title?: string;
  actions?: React.ReactNode;
  totalsOverride?: {
    distanceKm?: number;
    driveMinutes?: number;
  };
  compact?: boolean;
  /** When provided, middle stops get a nights stepper (0 = via). */
  onChangeNights?: (stopId: string, nights: number) => void;
  /** Where the leg figures came from — estimates are marked "≈". */
  legSource?: 'vehicle' | 'google' | 'estimate';
  /** Draw "Day N" bands inside the stop list (off when the day plan shows). */
  dayHeaders?: boolean;
  /** Extra controls under the header row (e.g. "Plan my nights"). */
  headerExtra?: React.ReactNode;
  /** Sit flat inside a parent card (no border of its own) and share its
   *  16px content inset, so the add-stop field and the stops line up. */
  flush?: boolean;
  /** A camp near each night that has no camp yet, keyed by stop id. */
  campSuggestions?: Record<string, { name: string; km: number }>;
  /** "Stay here": make the suggested camp that night's stop. */
  onUseCampSuggestion?: (stopId: string) => void;
  /** "Add a stop" on a leg longer than a day's driving. */
  onLongLeg?: (fromStopId: string) => void;
}

/* ── Google Maps deep-links ──────────────────────────────────────────────── */

const hasCoords = (s: TripStop): s is TripStop & { lat: number; lng: number } =>
  typeof s.lat === 'number' && typeof s.lng === 'number';

/** Directions to a single stop. */
function stopDirectionsUrl(s: TripStop): string | null {
  if (!hasCoords(s)) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;
}

/** Directions through the whole trip (origin → waypoints → destination).
 *  Google caps the URL at ~9 waypoints; we keep the first/last as origin/dest
 *  and pass the middle stops as waypoints (trimmed if there are too many).
 *  Exported for the printable day-sheet. */
export function tripDirectionsUrl(stops: TripStop[]): string | null {
  const pts = stops.filter(hasCoords);
  if (pts.length < 2) return null;
  const origin = pts[0];
  const dest = pts[pts.length - 1];
  const mids = pts.slice(1, -1).slice(0, 9);
  const params = new URLSearchParams({
    api: '1',
    origin: `${origin.lat},${origin.lng}`,
    destination: `${dest.lat},${dest.lng}`,
  });
  if (mids.length) {
    params.set('waypoints', mids.map((s) => `${s.lat},${s.lng}`).join('|'));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

type DropPosition = 'before' | 'after';

/** Vehicle choices — values match the server's vehicle types / routing
 *  profiles (routing_client.PROFILE_MAP). */
export const TRIP_VEHICLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'car', label: 'Car' },
  { value: '4wd', label: '4WD' },
  { value: 'caravan', label: 'Caravan' },
  { value: 'motorhome', label: 'Motorhome' },
];

/* ── Stop role visual config ─────────────────────────────────────────────── */
// Labels come from the stop's ROLE on the trip (utils/tripRoles), not its raw
// kind — every stop used to read "Waypoint", start and destination included.

// The planner's role colours (tailwind tp.role-*, planner/roles.ts): the
// same four colours as the planner's list and the map pins.
const ROLE_BADGE: Record<StopRole, string> = {
  start: 'bg-tp-role-start',
  end: 'bg-tp-role-end',
  overnight: 'bg-tp-role-night',
  via: 'bg-tp-role-via',
};

/** The legend over the stop list, in trip order. */
const LEGEND_ROLES: { role: StopRole; label: string }[] = [
  { role: 'start', label: 'Start' },
  { role: 'via', label: 'Passing through' },
  { role: 'overnight', label: 'Overnight' },
  { role: 'end', label: 'Finish' },
];

/** "Start" / "2 nights here [− +]" / "Passing through" / "Back home". One
 *  13px sentence-case line; the stepper only shows once a stop has a night
 *  (adding the first one lives in the stop's menu), so a stolen trip isn't
 *  a column of "No night" steppers. */
const StopRoleLine: React.FC<{
  stop: TripStop;
  role: StopRole;
  onChangeNights?: (nights: number) => void;
  /** The finish of a round trip reads "Back home", not "Finish". */
  homeEnd?: boolean;
}> = ({ stop, role, onChangeNights, homeEnd = false }) => {
  const nights = role === 'overnight' ? middleStopNights(stop) : 0;
  const canEdit = !!onChangeNights && role === 'overnight';
  const campNote = role === 'overnight' && stop.kind === 'camp' && <span className="text-stone-500"> · campsite</span>;
  if (!canEdit) {
    return (
      <span className="mt-0.5 block text-[13px] leading-5 text-stone-600">
        {stopRoleText(role, nights, { homeEnd })}
        {campNote}
      </span>
    );
  }
  // [−  2  +] nights here: the count sits between the buttons it changes.
  return (
    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-5 text-stone-600">
      {canEdit && (
        <span
          role="group"
          aria-label={`Nights at ${stop.name}`}
          className="inline-flex h-7 items-center overflow-hidden rounded-lg border border-stone-200 bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label={`One less night at ${stop.name}`}
            onClick={() => onChangeNights!(nights - 1)}
            className="flex h-7 w-7 min-h-0 items-center justify-center text-[15px] leading-none text-stone-600 transition-colors hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1558b0]/40"
          >
            −
          </button>
          <span
            className="min-w-[24px] px-1 text-center text-[14px] font-semibold tabular-nums text-stone-900"
            aria-live="polite"
          >
            {nights}
          </span>
          <button
            type="button"
            aria-label={`One more night at ${stop.name}`}
            disabled={nights >= MAX_NIGHTS_PER_STOP}
            onClick={() => onChangeNights!(nights + 1)}
            className="flex h-7 w-7 min-h-0 items-center justify-center text-[15px] leading-none text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1558b0]/40"
          >
            +
          </button>
        </span>
      )}
      <span>
        {nights === 1 ? 'night here' : 'nights here'}
        {campNote}
      </span>
    </span>
  );
};

function LoopBadgeGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 2l3 3-3 3" />
      <path d="M4 11V9a4 4 0 014-4h12" />
      <path d="M7 22l-3-3 3-3" />
      <path d="M20 13v2a4 4 0 01-4 4H4" />
    </svg>
  );
}

/* ── Per-stop overflow menu ──────────────────────────────────────────────── */
// Reorder (for touch + keyboard; mouse users can also drag the row), notes and
// remove live here, so the stop's name gets the row's full width instead of
// sharing it with four faint glyphs.

interface StopMenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  /** Opens in a new tab (Directions) instead of running onSelect. */
  href?: string;
}

const StopMenu: React.FC<{ stopName: string; items: StopMenuItem[] }> = ({ stopName, items }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const firstEnabled = items.findIndex((it) => !it.disabled);
    itemRefs.current[firstEnabled >= 0 ? firstEnabled : 0]?.focus();
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, items]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) requestAnimationFrame(() => buttonRef.current?.focus());
  };

  const onMenuKey = (e: React.KeyboardEvent) => {
    const enabled = itemRefs.current.filter((el, i) => el && !items[i]?.disabled) as HTMLButtonElement[];
    const i = enabled.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      enabled[(i + 1) % enabled.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      enabled[i <= 0 ? enabled.length - 1 : i - 1]?.focus();
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`More for ${stopName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={`-my-1.5 flex h-8 w-8 min-h-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1558b0]/40 ${
          open ? 'bg-stone-100 text-stone-800' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-800'
        }`}
      >
        <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true">
          <circle cx="5.5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="18.5" cy="12" r="1.7" />
        </svg>
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={`${stopName} options`}
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full z-30 mt-1 w-44 overflow-hidden rounded-xl border border-stone-200 bg-white py-1 shadow-lg"
        >
          {items.map((it, i) => it.href ? (
            <a
              key={it.label}
              ref={(el) => {
                itemRefs.current[i] = el as unknown as HTMLButtonElement | null;
              }}
              href={it.href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => close(false)}
              className="flex h-9 w-full items-center justify-between px-3 text-left text-[14px] text-stone-800 transition-colors hover:bg-stone-100 focus:bg-stone-100 focus:outline-none"
            >
              {it.label}
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-stone-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
              </svg>
            </a>
          ) : (
            <button
              key={it.label}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              onClick={() => {
                close(false);
                it.onSelect();
              }}
              className={`flex h-9 w-full min-h-0 items-center px-3 text-left text-[14px] transition-colors focus:outline-none disabled:cursor-default disabled:text-stone-300 ${
                it.danger
                  ? 'text-red-700 hover:bg-red-50 focus:bg-red-50'
                  : 'text-stone-800 hover:bg-stone-100 focus:bg-stone-100'
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */

const formatDrive = (minutes?: number): string => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '\u2014';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
};

/** One precision for every leg: whole km from 10 km up ("93 km", "110 km"),
 *  one decimal only under 10 ("4.2 km"). It used to print "110 km" beside
 *  "93.0 km". */
export const formatDistance = (distanceKm?: number): string => {
  if (typeof distanceKm !== 'number' || Number.isNaN(distanceKm)) return '\u2014';
  return distanceKm >= 10 ? `${Math.round(distanceKm)} km` : `${distanceKm.toFixed(1)} km`;
};

/** Swap a stop with its neighbour (▲ / ▼). Locked stops stay put. */
export function moveStop(stops: TripStop[], index: number, dir: -1 | 1): TripStop[] {
  const j = index + dir;
  if (j < 0 || j >= stops.length || stops[index]?.locked || stops[j]?.locked) return stops;
  const next = [...stops];
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

function reorderStops(
  stops: TripStop[],
  draggedId: string,
  targetId: string | null,
  position: DropPosition
): TripStop[] {
  const sourceIndex = stops.findIndex((stop) => stop.id === draggedId);
  if (sourceIndex === -1) return stops;

  const source = stops[sourceIndex];
  if (source.locked) return stops;

  const without = [...stops];
  without.splice(sourceIndex, 1);

  if (!targetId) {
    without.splice(without.length - 1, 0, source);
    return without;
  }

  const targetIndex = without.findIndex((stop) => stop.id === targetId);
  if (targetIndex === -1) return stops;

  const target = without[targetIndex];
  if (target.locked && target.kind === 'start' && position === 'before') {
    without.splice(targetIndex + 1, 0, source);
    return without;
  }

  if (target.locked && target.kind === 'end' && position === 'after') {
    without.splice(targetIndex, 0, source);
    return without;
  }

  const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
  without.splice(insertIndex, 0, source);
  return without;
}

/* ── Component ────────────────────────────────────────────────────────────── */

const TripPlannerLadder: React.FC<TripPlannerLadderProps> = ({
  stops,
  legs,
  onReorder,
  onRemoveStop,
  onEditNote,
  onEditName,
  startDate = null,
  onChangeStartDate,
  maxDriveMinutesPerDay = 360,
  onChangeMaxDriveHours,
  vehicleType = 'car',
  onChangeVehicleType,
  title = 'Trip Planner',
  actions,
  totalsOverride,
  compact = false,
  onChangeNights,
  legSource,
  dayHeaders = true,
  headerExtra,
  flush = false,
  campSuggestions,
  onUseCampSuggestion,
  onLongLeg,
}) => {
  const tripMapsUrl = tripDirectionsUrl(stops);
  // Straight-line fallback figures are flagged so nobody plans fuel off them.
  const approx = legSource === 'estimate' ? '≈ ' : '';
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsId = useId();
  const rowPad = flush ? 'px-4' : 'px-3';
  // "Melbourne → back to Melbourne" with nothing between: no drive yet.
  const unstarted = isUnstartedLoop(stops);
  const loop = isLoopTrip(stops);
  const placeCount = tripPlaceCount(stops);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<DropPosition>('after');

  const legByOrigin = useMemo(() => {
    const map = new Map<string, TripLeg>();
    legs.forEach((leg) => map.set(leg.fromId, leg));
    return map;
  }, [legs]);

  const totals = useMemo(() => summariseLegs(legs), [legs]);
  const totalStops = stops.length;

  // ── Day segmentation + per-night weather ─────────────────────────────────
  const days = useMemo(
    () => segmentIntoDays(stops, legs, maxDriveMinutesPerDay),
    [stops, legs, maxDriveMinutesPerDay]
  );
  const showDayHeaders = dayHeaders && totalStops >= 2 && (days.length >= 2 || !!startDate);
  // A day header renders above the first NEW stop of that day (the day's
  // origin is the previous day's overnight stop, already drawn above it).
  const dayHeaderByStopIndex = useMemo(() => {
    const m = new Map<number, TripDay>();
    if (showDayHeaders) {
      days.forEach((d) => m.set(d.index === 0 ? 0 : d.firstStopIndex + 1, d));
    }
    return m;
  }, [days, showDayHeaders]);

  const [dayForecasts, setDayForecasts] = useState<
    Record<number, { emoji: string; max: number | null } | null>
  >({});
  useEffect(() => {
    if (!startDate || days.length === 0) {
      setDayForecasts({});
      return;
    }
    let cancelled = false;
    days.forEach((day) => {
      const date = dateForTripDay(startDate, day.index);
      const overnight = stops[day.lastStopIndex];
      if (!date || !overnight || typeof overnight.lat !== 'number' || typeof overnight.lng !== 'number') {
        return;
      }
      fetchWeather(overnight.lat, overnight.lng).then((w) => {
        if (cancelled) return;
        const f = forecastForDate(w, date);
        setDayForecasts((prev) => ({
          ...prev,
          [day.index]: f ? { emoji: weatherEmoji(f.condition), max: f.temp_max_c } : null,
        }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [days, startDate, stops]);

  const formatDayDate = (dayIndex: number): string | null => {
    const iso = dateForTripDay(startDate, dayIndex);
    if (!iso) return null;
    return new Date(`${iso}T12:00:00`).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };
  const distanceValue =
    totalsOverride?.distanceKm ?? totals.totalDistanceKm;
  const driveValue =
    totalsOverride?.driveMinutes ?? totals.totalDriveMinutes;
  const totalDistanceLabel = formatDistance(distanceValue);
  const totalDriveLabel = formatDrive(driveValue);
  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  const getBadgeLabel = (index: number): string => String.fromCharCode(65 + index);

  const getBadgeClasses = (index: number): string => {
    return `${ROLE_BADGE[stopRole(stops, index)]} text-white`;
  };

  const handleDragStart = (stop: TripStop) => {
    if (stop.locked) return;
    setDraggingId(stop.id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setOverId(null);
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    stop: TripStop
  ) => {
    event.preventDefault();
    if (!draggingId || draggingId === stop.id) return;
    if (stop.locked && stop.id === stops[0].id) {
      setOverId(stop.id);
      setDropPosition('after');
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const before = event.clientY - rect.top < rect.height / 2;
    setOverId(stop.id);
    setDropPosition(before ? 'before' : 'after');
  };

  const handleDrop = (target: TripStop | null) => {
    if (!draggingId) return;
    const newStops = reorderStops(
      stops,
      draggingId,
      target ? target.id : null,
      dropPosition
    );
    onReorder(newStops);
    setDraggingId(null);
    setOverId(null);
  };

  /* ── Empty state ──────────────────────────────────────────────────────── */

  if (!stops.length) {
    return (
      <div className="w-full rounded-xl border border-dashed border-gray-200 bg-white px-4 py-5 text-center">
        <EnvironmentFilled className="text-2xl text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-700">{title}</p>
        <p className="mt-1 text-xs text-gray-500">
          Add stops from search results to build your trip.
        </p>
        {actions && <div className="mt-3 flex justify-center gap-2">{actions}</div>}
      </div>
    );
  }

  /* ── Main render ──────────────────────────────────────────────────────── */

  return (
    <div
      className={`flex w-full flex-col overflow-visible bg-white ${
        flush ? '' : 'rounded-xl border border-stone-200'
      }`}
    >
      {/* A round trip that hasn't left home has nothing to sum up yet: the
          one row below says it all ("Melbourne · Start and finish"). */}
      {!unstarted && (!flush || (totalStops >= 2 && (onChangeStartDate || onChangeVehicleType || onChangeMaxDriveHours || headerExtra))) && (
      <header className="border-b border-stone-100 bg-white">
        <div className="flex flex-col gap-1 px-4 py-3">
          {/* Flush (inside the planner panel): the panel's own header already
              names the trip and sums it up — don't say it twice. */}
          {!flush && (
          <>
          <h3 className="truncate text-[17px] font-semibold leading-6 tracking-tight text-stone-900">
            {totalStops === 1
              ? firstStop?.name ?? 'Start'
              : loop
                ? `Round trip from ${firstStop?.name ?? 'home'}`
                : <>{firstStop?.name ?? 'Start'} &rarr; {lastStop?.name ?? 'Finish'}</>}
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] leading-5 text-stone-600">
            {totalStops === 1 ? (
              <span>Add your next stop and we’ll map the drive.</span>
            ) : (
              <>
                <span className="font-medium text-stone-800">
                  {placeCount} stop{placeCount === 1 ? '' : 's'}
                </span>
                <span
                  className="inline-flex items-center gap-1"
                  data-testid="trip-total-distance"
                  title={approx ? 'Straight-line estimate, not the road distance' : undefined}
                >
                  <CarOutlined className="text-[13px] text-stone-500" />
                  {approx}{totalDistanceLabel}
                  {approx && <span>straight-line</span>}
                </span>
                {/* A drive time off a straight line would be a guess dressed
                    as a fact — only routed figures get a clock. */}
                {!approx && (
                  <span className="inline-flex items-center gap-1" data-testid="trip-total-drive">
                    <ClockCircleOutlined className="text-[13px] text-stone-500" />
                    {totalDriveLabel}
                  </span>
                )}
                {tripMapsUrl && (
                  <a
                    href={tripMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-[#1558b0] hover:underline"
                    title="Open the whole trip in Google Maps"
                  >
                    <CompassOutlined className="text-[13px]" />
                    Google Maps
                  </a>
                )}
              </>
            )}
          </div>
          </>
          )}
          {/* Date, vehicle and drive/day fold into ONE settings row, so the
              itinerary starts near the top on a phone. */}
          {totalStops >= 2 && (onChangeStartDate || onChangeVehicleType || onChangeMaxDriveHours) && (
            <div className={flush ? '' : 'mt-1'}>
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                aria-expanded={settingsOpen}
                aria-controls={settingsId}
                data-testid="trip-settings-toggle"
                className="flex h-9 w-full min-h-0 items-center gap-2 rounded-lg border border-solid border-stone-200 bg-white px-3 text-left text-[13px] text-stone-700 transition-colors hover:border-stone-300 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1558b0]/40"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 flex-none text-stone-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
                  <circle cx="16" cy="7" r="2" />
                  <circle cx="10" cy="17" r="2" />
                </svg>
                <span className="flex-none font-medium text-stone-900">Trip settings</span>
                <span className="min-w-0 flex-1 truncate text-right text-stone-600">
                  {tripSettingsSummary({
                    vehicleLabel: onChangeVehicleType
                      ? TRIP_VEHICLE_OPTIONS.find((o) => o.value === vehicleType)?.label ?? vehicleType
                      : null,
                    startDateLabel: startDate
                      ? new Date(`${startDate}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
                      : null,
                    driveHours: onChangeMaxDriveHours ? Math.round(maxDriveMinutesPerDay / 60) : null,
                  })}
                </span>
                <svg viewBox="0 0 24 24" className={`h-4 w-4 flex-none text-stone-400 transition-transform duration-200 motion-reduce:transition-none ${settingsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {settingsOpen && (
                <div id={settingsId} className="mt-2 flex flex-col gap-3 rounded-lg bg-stone-50 p-3">
                  {onChangeStartDate && (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[13px] font-medium text-stone-700">Leaving</span>
                      <DateButton
                        size="sm"
                        label="Departs"
                        placeholder="Add a date"
                        value={startDate}
                        onChange={onChangeStartDate}
                      />
                    </div>
                  )}
                  {onChangeVehicleType && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[13px] font-medium text-stone-700" id={`${settingsId}-vehicle`}>Vehicle</span>
                      <div
                        role="radiogroup"
                        aria-labelledby={`${settingsId}-vehicle`}
                        title="What you’re driving or towing — shapes the route and which camps suit each night"
                        className="inline-flex h-8 items-center rounded-lg bg-stone-200/60 p-0.5"
                      >
                        {(TRIP_VEHICLE_OPTIONS.some((o) => o.value === vehicleType)
                          ? TRIP_VEHICLE_OPTIONS
                          : [...TRIP_VEHICLE_OPTIONS, { value: vehicleType, label: vehicleType }]
                        ).map((o) => {
                          const on = o.value === vehicleType;
                          return (
                            <button
                              key={o.value}
                              type="button"
                              role="radio"
                              aria-checked={on}
                              onClick={() => onChangeVehicleType(o.value)}
                              className={`h-7 min-h-0 rounded-md px-2.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1558b0]/40 ${
                                on ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600 hover:text-stone-900'
                              }`}
                            >
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {onChangeMaxDriveHours && (
                    <label
                      className="flex items-center justify-between gap-3 text-[13px] font-medium text-stone-700"
                      title="Daily driving budget — how the trip splits into days"
                    >
                      Driving a day
                      <select
                        value={String(Math.round(maxDriveMinutesPerDay / 60))}
                        onChange={(e) => onChangeMaxDriveHours(Number(e.target.value))}
                        className="h-8 rounded-lg border border-stone-300 bg-white px-2 text-[13px] font-medium text-stone-900 focus:border-[#1558b0] focus:outline-none focus:ring-2 focus:ring-[#1558b0]/20"
                      >
                        {[3, 4, 5, 6, 7, 8, 10].map((h) => (
                          <option key={h} value={h}>{h} hours</option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
          {headerExtra && totalStops >= 2 && <div className="pt-1">{headerExtra}</div>}
        </div>
      </header>
      )}

      {/* What the stop colours mean — only the roles this trip has. */}
      {!unstarted && stops.length >= 2 && (
        <ul
          className={`flex flex-wrap items-center gap-x-3 gap-y-1 pt-2.5 text-[12px] leading-4 text-stone-500 ${rowPad}`}
          aria-label="Stop colours"
          data-testid="stop-legend"
        >
          {LEGEND_ROLES.filter((r) => stops.some((_, i) => stopRole(stops, i) === r.role)).map((r) => (
            <li key={r.role} className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${ROLE_BADGE[r.role]}`} />
              {r.role === 'end' && loop ? 'Back home' : r.label}
            </li>
          ))}
        </ul>
      )}

      {/* Stop list — unified timeline */}
      <div className="flex flex-col">
        {stops.map((stop, index) => {
          // An unstarted round trip is ONE row ("Melbourne · Start and
          // finish"), not the same town as A and B.
          if (unstarted && index > 0) return null;
          const legToNext = legByOrigin.get(stop.id);
          const isLast = index === stops.length - 1;
          const isDragging = draggingId === stop.id;
          const isTarget = overId === stop.id;

          const dayHeader = dayHeaderByStopIndex.get(index);
          const dayForecast = dayHeader ? dayForecasts[dayHeader.index] : null;
          const dayDateLabel = dayHeader ? formatDayDate(dayHeader.index) : null;

          return (
            <div key={stop.id} className="relative">
              {dayHeader && (
                <div className={`flex items-center justify-between gap-2 border-y border-sky-100 bg-sky-50/70 py-1.5 ${rowPad}`}>
                  <span className="text-[13px] font-semibold text-sky-800">
                    Day {dayHeader.index + 1}
                    {dayDateLabel && (
                      <span className="ml-1.5 font-normal text-sky-700">
                        {dayDateLabel}
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-[13px] text-sky-800">
                    {typeof dayHeader.distanceKm === 'number' && dayHeader.distanceKm > 0 && (
                      <span>{formatDistance(dayHeader.distanceKm)}</span>
                    )}
                    {typeof dayHeader.driveMinutes === 'number' && dayHeader.driveMinutes > 0 && (
                      <span>{formatDrive(dayHeader.driveMinutes)}</span>
                    )}
                    {dayForecast && (
                      <span title="Forecast at that night's stop">
                        {dayForecast.emoji}
                        {typeof dayForecast.max === 'number' ? ` ${Math.round(dayForecast.max)}°` : ''}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {/* Drop indicator: before */}
              {isTarget && dropPosition === 'before' && (
                <div className="absolute -top-[1px] left-0 right-3 z-10 flex items-center">
                  <div className="ml-[11px] h-2.5 w-2.5 shrink-0 rounded-full border-2 border-accent bg-white" />
                  <div className="h-[2px] flex-1 rounded-full bg-accent" />
                </div>
              )}

              <div
                draggable={!stop.locked && editingNameId !== stop.id}
                onDragStart={() => handleDragStart(stop)}
                onDragEnd={handleDragEnd}
                onDragOver={(event) => handleDragOver(event, stop)}
                onDrop={(event) => {
                  event.preventDefault();
                  handleDrop(stop);
                }}
                className={[
                  `group flex gap-3 ${rowPad} transition-colors hover:bg-stone-50/80`,
                  stop.locked
                    ? 'cursor-default'
                    : 'cursor-grab active:cursor-grabbing',
                  isDragging ? 'bg-[#e3f2fd]/60' : '',
                ].join(' ')}
              >
                {/* Timeline column */}
                <div className="flex shrink-0 flex-col items-center w-8">
                  <div className={compact ? 'h-2 shrink-0' : 'h-3 shrink-0'} />
                  <div
                    className={[
                      'flex items-center justify-center rounded-full text-xs font-bold',
                      'h-8 w-8 ring-2 ring-white shadow-sm',
                      getBadgeClasses(index),
                    ].join(' ')}
                  >
                    {unstarted ? <LoopBadgeGlyph /> : getBadgeLabel(index)}
                  </div>
                  {!isLast && !unstarted && (
                    <div
                      className="w-0.5 flex-1 rounded-full bg-gray-200"
                      style={{ minHeight: '12px' }}
                    />
                  )}
                  {(isLast || unstarted) && <div className={compact ? 'h-2 shrink-0' : 'h-3 shrink-0'} />}
                </div>

                {/* Content column */}
                <div className={`flex-1 min-w-0 ${compact ? 'py-1.5' : 'py-2.5'}`}>
                  {/* Name + controls */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {onEditName && editingNameId === stop.id ? (
                        <input
                          type="text"
                          defaultValue={stop.name}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            if (e.key === 'Escape') setEditingNameId(null);
                          }}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== stop.name) onEditName(stop.id, v);
                            setEditingNameId(null);
                          }}
                          className="w-full rounded-md border border-sky-300 bg-white px-1.5 py-0.5 text-[15px] font-semibold text-gray-900 focus:outline-none focus:ring-1 focus:ring-sky-300"
                        />
                      ) : onEditName ? (
                        <button
                          type="button"
                          title="Rename this stop"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingNameId(stop.id);
                          }}
                          className="block min-h-0 max-w-full truncate text-left text-[15px] font-semibold leading-5 text-stone-900 decoration-stone-300 decoration-dotted underline-offset-2 hover:underline"
                        >
                          {stop.name}
                        </button>
                      ) : (
                        <p className="truncate text-[15px] font-semibold leading-5 text-stone-900">
                          {stop.name}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {stop.locked && (
                        <LockOutlined className="text-[13px] text-stone-500" aria-label="Locked" />
                      )}
                      {(() => {
                        const items: StopMenuItem[] = [];
                        const role = stopRole(stops, index);
                        const directions = stopDirectionsUrl(stop);
                        if (directions && !unstarted) {
                          items.push({ label: 'Directions', href: directions, onSelect: () => {} });
                        }
                        if (onChangeNights && role === 'via') {
                          items.push({ label: 'Stay a night here', onSelect: () => onChangeNights(stop.id, 1) });
                        }
                        if (!stop.locked && stops.length > 1 && !unstarted) {
                          items.push(
                            { label: 'Move earlier', disabled: index === 0, onSelect: () => onReorder(moveStop(stops, index, -1)) },
                            { label: 'Move later', disabled: index === stops.length - 1, onSelect: () => onReorder(moveStop(stops, index, 1)) },
                          );
                        }
                        if (onEditNote) {
                          items.push({ label: stop.notes ? 'Edit note' : 'Add a note', onSelect: () => setEditingNoteId(stop.id) });
                        }
                        if (onEditName) {
                          items.push({ label: 'Rename', onSelect: () => setEditingNameId(stop.id) });
                        }
                        if (unstarted) {
                          // The way home is the second, hidden stop.
                          const home = stops[stops.length - 1];
                          if (onRemoveStop && home && !home.locked) {
                            items.push({ label: 'Make it one-way', onSelect: () => onRemoveStop(home.id) });
                          }
                        } else if (onRemoveStop && !stop.locked) {
                          items.push({ label: 'Remove stop', danger: true, onSelect: () => onRemoveStop(stop.id) });
                        }
                        return items.length ? <StopMenu stopName={stop.name} items={items} /> : null;
                      })()}
                    </div>
                  </div>

                  {/* Role on the trip + nights here */}
                  {unstarted ? (
                    <span className="mt-0.5 block text-[13px] leading-5 text-stone-600" data-testid="loop-start-row">
                      Start and finish
                    </span>
                  ) : (
                    <StopRoleLine
                      stop={stop}
                      role={stopRole(stops, index)}
                      homeEnd={loop && isLast}
                      onChangeNights={
                        onChangeNights ? (n) => onChangeNights(stop.id, n) : undefined
                      }
                    />
                  )}

                  {/* A night with no camp yet: the nearest one, one tap to
                      make it the stop. */}
                  {!unstarted && campSuggestions?.[stop.id] && stopRole(stops, index) === 'overnight' && (
                    <span className="mt-1.5 flex items-center gap-2 rounded-lg bg-stone-50 py-1.5 pl-2.5 pr-1.5 text-[13px] leading-[18px] text-stone-700 ring-1 ring-inset ring-stone-200" data-testid="camp-suggestion">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 flex-none text-emerald-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M2.5 20h19M12 5L4.5 20M12 5l7.5 15M9.5 20l2.5-5 2.5 5" />
                      </svg>
                      <span className="line-clamp-2 min-w-0 flex-1">
                        <span className="font-semibold text-stone-900">{campSuggestions[stop.id].name}</span>
                        <span className="text-stone-500"> · {campSuggestions[stop.id].km < 1 ? '<1' : Math.round(campSuggestions[stop.id].km)} km</span>
                      </span>
                      {onUseCampSuggestion && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUseCampSuggestion(stop.id);
                          }}
                          className="h-7 min-h-0 flex-none whitespace-nowrap rounded-md px-2 text-[13px] font-semibold text-[#1558b0] transition-colors hover:bg-[#1558b0]/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1558b0]/40"
                          aria-label={`Stay at ${campSuggestions[stop.id].name}`}
                        >
                          Stay here
                        </button>
                      )}
                    </span>
                  )}

                  {/* The drive to the next stop, as one quiet line. Directions
                      live in the stop's menu, not under every row. */}
                  {!unstarted && !isLast && legToNext && isMeaningfulLeg(legToNext.distanceKm) && (
                    <span
                      className="mt-1 flex items-center gap-1.5 text-[13px] leading-5 text-stone-500"
                      data-testid="trip-leg"
                      title={legSource === 'estimate' ? 'Straight-line estimate, not the road distance' : 'Drive to the next stop'}
                    >
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 flex-none" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 4v16M6 14l6 6 6-6" />
                      </svg>
                      <span>
                        {approx}{formatDistance(legToNext.distanceKm)}
                        {!approx && <> · {formatDrive(legToNext.driveMinutes)}</>}
                      </span>
                    </span>
                  )}

                  {/* A leg over a day's driving makes a curated trip look
                      sloppy: say so, and offer the fix. */}
                  {!unstarted && !isLast && legToNext && !approx && isLongLeg(legToNext, Math.min(maxDriveMinutesPerDay, 330)) && (
                    <span className="mt-1.5 flex items-center gap-2 text-[13px] leading-5 text-amber-800" data-testid="long-leg">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 flex-none" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                      <span className="min-w-0 flex-1">Long drive. Break it up?</span>
                      {onLongLeg && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLongLeg(stop.id);
                          }}
                          className="h-8 min-h-0 flex-none rounded-lg px-2 text-[13px] font-semibold text-[#1558b0] transition-colors hover:bg-[#1558b0]/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1558b0]/40"
                        >
                          Add a stop
                        </button>
                      )}
                    </span>
                  )}

                  {/* Per-stop note: shown when there is one; the menu adds
                      one. No empty "Add a note" row under every stop. */}
                  {onEditNote && editingNoteId === stop.id ? (
                    <textarea
                      defaultValue={stop.notes ?? ''}
                      autoFocus
                      rows={2}
                      aria-label={`Note for ${stop.name}`}
                      placeholder="Book ahead, fuel up, the bakery…"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setEditingNoteId(null);
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          (e.target as HTMLTextAreaElement).blur();
                        }
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (stop.notes ?? '')) onEditNote(stop.id, v);
                        setEditingNoteId(null);
                      }}
                      className="mt-1.5 w-full resize-none rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[13px] text-stone-800 placeholder:text-stone-400 focus:border-[#1558b0] focus:outline-none focus:ring-4 focus:ring-[#1558b0]/15"
                    />
                  ) : (
                    stop.notes && (
                      onEditNote ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingNoteId(stop.id);
                          }}
                          className="mt-1.5 flex w-full min-h-0 items-start gap-1.5 rounded-md text-left text-[13px] leading-5 text-stone-600 hover:text-stone-900"
                          title="Edit note"
                        >
                          <svg viewBox="0 0 24 24" className="mt-0.5 h-3.5 w-3.5 flex-none text-stone-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M4 20h4L19 9l-4-4L4 16v4z" />
                          </svg>
                          <span className="line-clamp-2">{stop.notes}</span>
                        </button>
                      ) : (
                        <p className="mt-1.5 text-[13px] text-stone-600">{stop.notes}</p>
                      )
                    )
                  )}
                </div>
              </div>

              {/* Drop indicator: after */}
              {isTarget && dropPosition === 'after' && (
                <div className="absolute -bottom-[1px] left-0 right-3 z-10 flex items-center">
                  <div className="ml-[11px] h-2.5 w-2.5 shrink-0 rounded-full border-2 border-accent bg-white" />
                  <div className="h-[2px] flex-1 rounded-full bg-accent" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions footer */}
      {actions && (
        <div className={`border-t border-stone-100 py-3 ${rowPad}`}>
          {actions}
        </div>
      )}
    </div>
  );
};

export default TripPlannerLadder;
